/**
 * ============================================================================
 * RetireDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: dual-sig + ≥200 char rationale gate to retire (or decommission)
 *          equipment. Mirrors the supplier DisqualifyDialog pattern. The
 *          server enforces both the rationale minimum and the "distinct
 *          signers" rule; we mirror them in the UI for fast feedback.
 *
 * A11Y: live counter announced via aria-live="polite"; the submit button is
 *  disabled until both signers and the rationale length all check out.
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './EquipmentDetail.module.css';
import { useRetireMutation } from './useRetireMutation';

const RATIONALE_MIN = 200;

export interface RetireDialogProps {
  equipmentId: string;
  equipmentName: string;
  equipmentTag: string;
  onClose: () => void;
  onRetired: () => void;
}

export function RetireDialog({
  equipmentId,
  equipmentName,
  equipmentTag,
  onClose,
  onRetired,
}: RetireDialogProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLTextAreaElement | null>(null);

  const retireM = useRetireMutation();
  const [rationale, setRationale] = useState('');
  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [decommission, setDecommission] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rationaleLen = rationale.trim().length;
  const sigOk =
    sigA.trim() !== '' && sigB.trim() !== '' && sigA.trim() !== sigB.trim();
  const canSubmit = rationaleLen >= RATIONALE_MIN && sigOk;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (rationaleLen < RATIONALE_MIN) {
      setError(
        t('equipment.detail.retire.errors.rationaleShort', {
          defaultValue:
            'Rationale must be at least {{min}} characters (currently {{len}}).',
          min: nf.format(RATIONALE_MIN),
          len: nf.format(rationaleLen),
        }),
      );
      return;
    }
    if (!sigOk) {
      setError(
        t('equipment.detail.retire.errors.dualSign', {
          defaultValue:
            'Two distinct signature bundle ids are required (one per signer).',
        }),
      );
      return;
    }
    try {
      await retireM.mutateAsync({
        equipmentId,
        rationale: rationale.trim(),
        signature_bundle_id_a: sigA.trim(),
        signature_bundle_id_b: sigB.trim(),
        decommission,
      });
      onRetired();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('equipment.detail.retire.errors.serverFailed', {
              defaultValue: 'Server rejected the retirement.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.detail.retire.title', {
              defaultValue: 'Retire equipment',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.detail.retire.subtitle', {
              defaultValue:
                'Retiring {{tag}} ({{name}}) is high-blast-radius — it blocks the equipment from any production use and quarantines dependent records. This action requires two distinct signers and a rationale of at least 200 characters.',
              tag: equipmentTag,
              name: equipmentName,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.dualSigWarn} role="note">
            <strong>
              {equipmentTag} — {equipmentName}
            </strong>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="eq-ret-rat" className={dialogStyles.label}>
              {t('equipment.detail.retire.rationale', {
                defaultValue: 'Rationale',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="eq-ret-rat"
              ref={firstRef}
              className={styles.dialogTextarea}
              value={rationale}
              rows={6}
              onChange={(e) => setRationale(e.target.value)}
              aria-describedby="eq-ret-counter"
              required
            />
            <div
              id="eq-ret-counter"
              className={styles.charCounter}
              aria-live="polite"
            >
              {t('equipment.detail.retire.counter', {
                defaultValue: '{{len}} / {{min}} characters',
                len: nf.format(rationaleLen),
                min: nf.format(RATIONALE_MIN),
              })}
            </div>
          </div>

          <div className={styles.dualSigGrid}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-ret-a" className={dialogStyles.label}>
                {t('equipment.detail.retire.sigA', {
                  defaultValue: 'Signature bundle id — SIGNER A',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="eq-ret-a"
                className={dialogStyles.input}
                value={sigA}
                onChange={(e) => setSigA(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-ret-b" className={dialogStyles.label}>
                {t('equipment.detail.retire.sigB', {
                  defaultValue: 'Signature bundle id — SIGNER B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="eq-ret-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label
              htmlFor="eq-ret-dec"
              className={dialogStyles.label}
              style={{ cursor: 'pointer' }}
            >
              <input
                id="eq-ret-dec"
                type="checkbox"
                checked={decommission}
                onChange={(e) => setDecommission(e.target.checked)}
                style={{ marginInlineEnd: '0.5rem' }}
              />
              {t('equipment.detail.retire.decommission', {
                defaultValue:
                  'Decommission instead of retire (permanent — no return path)',
              })}
            </label>
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={retireM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={retireM.isPending}
              disabled={!canSubmit}
            >
              {decommission
                ? t('equipment.detail.retire.submitDecommission', {
                    defaultValue: 'Decommission',
                  })
                : t('equipment.detail.retire.submit', {
                    defaultValue: 'Retire',
                  })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
